import { retrieveSharedReportsApi } from '../utils/api';
import { SHARED_REPORTS } from '../constants/actionTypes';
import {
  REPORT_STATUS_AVAILABLE,
  REPORT_STATUS_NOT_AVAILABLE,
  RESPONSE_TYPE_GATEWAY_TIMEOUT,
  RESPONSE_TYPE_IP_ADDRESS_FORBIDDEN,
  RESPONSE_TYPE_REPORT_EXPIRED,
  RESPONSE_TYPE_USER_PREVIOUSLY_IDENTIFIED,
} from '../constants/statusConstants';
import { requestApplicationManagerToIdv } from './initApplicationActionCreators';
import { currentApplicationInitState } from '../reducers/currentApplication';
import { logError } from '@zg-rentals/log-error';

const DEFAULT_REPORT_STATUSES = {
  creditReportStatus: REPORT_STATUS_NOT_AVAILABLE,
  backgroundReportStatus: REPORT_STATUS_NOT_AVAILABLE,
  evictionReportStatus: REPORT_STATUS_NOT_AVAILABLE,
  criminalReportStatus: REPORT_STATUS_NOT_AVAILABLE,
};

let skipFinally = false;

export function updateSharedReports(payload) {
  return { type: SHARED_REPORTS, payload };
}

export const retrieveSharedReports =
  ({ applicantId, applicationId, propertyId }) =>
  (dispatch) => {
    // Initialise statuses as not available by default.
    let sharedReportStatuses = DEFAULT_REPORT_STATUSES;

    dispatch(
      retrieveSharedReportsApi({
        zuid: applicantId,
        applicationId,
        listingAlias: propertyId,
        requestingClient: 'ra-landlord-web-ssr',
      }),
    )
      .then(
        (response = {}) => {
          const { data = {} } = response;

          if (!data?.success) {
            logError({ context: 'retrieveSharedReports error: ', error: response });
            if (data.responseType === 'APPLICATION_MANAGER_NOT_ID_VERIFIED_ERROR') {
              logError({ error: 'Application manager not ID verified.' });
              dispatch(requestApplicationManagerToIdv());
              /**
               * We're skipping the finally block to prevent the report statuses from
               * being set to unavailable because we don't know whether that is actually
               * true based on the APPLICATION_MANAGER_NOT_ID_VERIFIED_ERROR response type.
               * And this prevents the reports unavailable alerts from displaying unnecessarily.
               * */
              skipFinally = true;
            }
          } else {
            const {
              creditReport: { reportStatus: creditReportStatus } = {},
              backgroundReport: {
                reportStatus: backgroundReportStatus,
                evictionReport: { reportStatus: evictionReportStatus } = {},
                criminalReport: { reportStatus: criminalReportStatus } = {},
              } = {},
            } = data;

            sharedReportStatuses = {
              creditReportStatus,
              backgroundReportStatus,
              evictionReportStatus,
              criminalReportStatus,
            };
          }

          dispatch(
            updateSharedReports({
              applicantId,
              sharedReports: data, // To be passed to screening-js.
            }),
          );
        },
        (serverError) => {
          const responseType = serverError?.response?.data?.responseType;
          if (responseType === RESPONSE_TYPE_IP_ADDRESS_FORBIDDEN) {
            /**
             * If IP address is forbidden, just tell ALW that all is
             * hunky dory, and let screening-js handle it.
             */
            sharedReportStatuses = {
              creditReportStatus: REPORT_STATUS_AVAILABLE,
              backgroundReportStatus: REPORT_STATUS_AVAILABLE,
              evictionReportStatus: REPORT_STATUS_AVAILABLE,
              criminalReportStatus: REPORT_STATUS_AVAILABLE,
            };

            // Screening-js just needs the response type.
            dispatch(
              updateSharedReports({
                applicantId,
                sharedReports: {
                  responseType: RESPONSE_TYPE_IP_ADDRESS_FORBIDDEN,
                },
              }),
            );
          } else if (responseType === RESPONSE_TYPE_GATEWAY_TIMEOUT) {
            dispatch(
              updateSharedReports({
                applicantId,
                sharedReports: {
                  responseType: RESPONSE_TYPE_GATEWAY_TIMEOUT,
                },
              }),
            );
          } else if (responseType === RESPONSE_TYPE_REPORT_EXPIRED) {
            // Set statuses to available, let ALW check expiration date to show correct status
            sharedReportStatuses = {
              creditReportStatus: REPORT_STATUS_AVAILABLE,
              backgroundReportStatus: REPORT_STATUS_AVAILABLE,
              evictionReportStatus: REPORT_STATUS_AVAILABLE,
              criminalReportStatus: REPORT_STATUS_AVAILABLE,
            };

            dispatch(
              updateSharedReports({
                applicantId,
                sharedReports: {
                  responseType: RESPONSE_TYPE_REPORT_EXPIRED,
                },
              }),
            );
          } else if (RESPONSE_TYPE_USER_PREVIOUSLY_IDENTIFIED) {
            // in this case, we won't set sharedReportStatuses to any values other than the initial state
            // because we don't know if the reports are actually available, expired, etc
            // this will provide the same experience to Irene as if she has not IDV'd yet
            sharedReportStatuses = currentApplicationInitState.sharedReportStatuses;
          }

          logError({ context: 'retrieveSharedReports server error', error: serverError });
        },
      )
      .finally(() => {
        if (!skipFinally) {
          dispatch(
            updateSharedReports({
              applicantId,
              sharedReportStatuses,
            }),
          );
        }
      });
  };
